import { Card, Pill, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { CardContent, Stack, useTheme } from "@mui/material";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { openZendeskMessaging } from "@src/appV2/lib/ZendeskMessaging/openZendeskMessaging";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { format } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { useInView } from "react-intersection-observer";

import { type PlacementCandidate } from "../../PlacementCandidate/types";
import { usePlacementCandidateBanContactSupport } from "../hooks/usePlacementCandidateBanContactSupport";

interface RestrictedAccessBannerProps {
  placementCandidateBan: NonNullable<PlacementCandidate["mostRecentBan"]>;
  workerTimezone: string;
}

export function RestrictedAccessBanner(props: RestrictedAccessBannerProps) {
  const { placementCandidateBan, workerTimezone } = props;
  const theme = useTheme();
  const { expiresAt, reason } = placementCandidateBan;

  const { mutateAsync: contactSupport, isLoading: isContactingSupport } =
    usePlacementCandidateBanContactSupport({});

  const { ref } = useInView({
    triggerOnce: true,
    threshold: 0,
    onChange: (inView) => {
      if (!inView) {
        return;
      }

      logEvent(APP_V2_APP_EVENTS.PLACEMENT_RESTRICTED_ACCESS_BANNER_VIEWED, {
        placementCandidateBan,
      });
    },
  });

  const handleContactSupportClick = async () => {
    logEvent(APP_V2_APP_EVENTS.PLACEMENT_RESTRICTED_ACCESS_BANNER_CONTACT_SUPPORT_CLICKED, {
      placementCandidateBan,
    });
    const { id: banId } = placementCandidateBan;

    await Promise.all([contactSupport({ banId }), openZendeskMessaging()]);
  };

  return (
    <Card
      ref={ref}
      sx={{
        px: 9,
        py: "90px",
        background: theme.palette.tiers?.limited.background,
        position: "relative",
      }}
    >
      <CardContent component={Stack} spacing={10} alignItems="center">
        <Stack spacing={5} sx={{ alignItems: "center", textAlign: "center" }}>
          {isDefined(expiresAt) && (
            <Pill
              iconType="clock-filled"
              label={`Lifting ${format(utcToZonedTime(expiresAt, workerTimezone), "MMMM d")}`}
              size="small"
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.4)",
              }}
            />
          )}

          <Text semibold variant="h4" color={(theme) => theme.palette.intent?.error.text}>
            Your account is restricted
          </Text>

          <Text variant="body2">{reason}</Text>
        </Stack>
        <LoadingButton
          variant="outlined"
          isLoading={isContactingSupport}
          sx={{ width: "fit-content", border: "none" }}
          onClick={handleContactSupportClick}
        >
          Contact Support
        </LoadingButton>
      </CardContent>
    </Card>
  );
}
